import type { ColumnKind } from "@/lib/types";

/** The fields these decisions read; a `BoardColumn` has them all. */
export interface TargetColumn {
  id: string;
  name: string;
  kind: ColumnKind;
  position: number;
}

const byPosition = <C extends TargetColumn>(columns: readonly C[]): C[] => [...columns].sort((a, b) => a.position - b.position);

/** Where "Mark done" sends a card: the board's first done-kind column — kind, never a name, since core stamps `completedAt` on kind. */
export function doneTarget<C extends TargetColumn>(columns: readonly C[]): C | null {
  return byPosition(columns).find((column) => column.kind === "done") ?? null;
}

/** Where "Reopen" sends a done card: the first active-kind column, else the leftmost that is not done. */
export function reopenTarget<C extends TargetColumn>(columns: readonly C[]): C | null {
  const ordered = byPosition(columns);
  return ordered.find((column) => column.kind === "active") ?? ordered.find((column) => column.kind !== "done") ?? null;
}

export type QuickAction<C extends TargetColumn> = { action: "done" | "reopen"; target: C };

/**
 * The one-tap action a card in `column` offers: Mark done from a review-kind
 * column, Reopen from a done-kind one, nothing elsewhere (the state menu covers
 * those). Null as well when the board has no column to go to.
 */
export function quickActionFor<C extends TargetColumn>(column: TargetColumn, columns: readonly C[]): QuickAction<C> | null {
  if (column.kind === "review") {
    const target = doneTarget(columns);
    return target ? { action: "done", target } : null;
  }
  if (column.kind === "done") {
    const target = reopenTarget(columns);
    return target && target.id !== column.id ? { action: "reopen", target } : null;
  }
  return null;
}
